import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { MdVideocam } from "react-icons/md";
import { clearCamSession, getCamToken } from "../cameraAuth";
import {
  TERMS_PUBLISHED_AT,
  TERMS_SUBTITLE,
  TERMS_TEXT,
  TERMS_TITLE,
  TERMS_VERSION,
} from "../terms";

/**
 * Porta de entrada do portal: no primeiro acesso (e sempre que uma versão nova
 * do termo é publicada) o cliente precisa aceitar o Termo de Uso e
 * Responsabilidade antes de ver qualquer coisa.
 *
 * O bloqueio de verdade é do backend (middleware RequireTermsAccepted); aqui
 * evitamos que o portal chame a API só para tomar 403.
 */

type Linha =
  | { tipo: "titulo"; texto: string }
  | { tipo: "opcao"; texto: string }
  | { tipo: "item"; texto: string }
  | { tipo: "paragrafo"; texto: string };

/** Quebra o texto do termo nos blocos que a tela sabe renderizar. */
function parseTermo(texto: string): Linha[] {
  return texto
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((linha) => {
      if (/^\d+\.\s/.test(linha)) return { tipo: "titulo" as const, texto: linha };
      if (linha.startsWith("☐")) return { tipo: "opcao" as const, texto: linha };
      if (linha.startsWith("- "))
        return { tipo: "item" as const, texto: linha.slice(2) };
      return { tipo: "paragrafo" as const, texto: linha };
    });
}

export const TermsGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const base = process.env.REACT_APP_URL;

  const [estado, setEstado] = useState<"verificando" | "pendente" | "ok">(
    "verificando",
  );
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // Só libera o "Concordo" depois de o texto ter sido rolado até o fim.
  const [lido, setLido] = useState(false);
  const [confirmandoRecusa, setConfirmandoRecusa] = useState(false);
  const textoRef = useRef<HTMLDivElement>(null);

  const linhas = useMemo(() => parseTermo(TERMS_TEXT), []);

  const sair = useCallback(() => {
    clearCamSession();
    navigate("/Cameras/Login", { replace: true });
  }, [navigate]);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const res = await axios.get(`${base}/cameras/me`, {
          headers: { Authorization: `Bearer ${getCamToken()}` },
          // Sem timeout esta chamada fica pendente para sempre e o portal
          // trava no "Carregando…" — o cliente só sai dali dando refresh até
          // dar sorte. Com o teto, o catch abaixo assume a falha de rede e
          // libera a tela (o backend continua barrando quem não aceitou).
          timeout: 15_000,
        });
        if (cancelado) return;
        setEstado(res.data?.termsAccepted ? "ok" : "pendente");
      } catch (e: any) {
        if (cancelado) return;
        if (e?.response?.status === 401) {
          sair();
          return;
        }
        // Falha de rede: não trava o cliente fora do portal — o backend barra
        // as rotas do serviço de qualquer forma se o termo não estiver aceito.
        setEstado("ok");
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [base, sair]);

  /** Marca como lido quando a rolagem chega ao fim do texto. */
  const aoRolar = () => {
    const el = textoRef.current;
    if (!el || lido) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setLido(true);
  };

  // Texto curto o bastante para caber sem rolagem (telas grandes): já liberado.
  useEffect(() => {
    if (estado !== "pendente") return;
    const el = textoRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 24) setLido(true);
  }, [estado]);

  const concordar = async () => {
    setErro(null);
    setEnviando(true);
    try {
      await axios.post(
        `${base}/cameras/terms/accept`,
        { version: TERMS_VERSION },
        { headers: { Authorization: `Bearer ${getCamToken()}` } },
      );
      setEstado("ok");
    } catch (e: any) {
      if (e?.response?.status === 401) {
        sair();
        return;
      }
      setErro(
        e?.response?.data?.message ||
          "Não foi possível registrar o aceite. Tente novamente.",
      );
    } finally {
      setEnviando(false);
    }
  };

  if (estado === "verificando") {
    return (
      <div className="min-h-screen flex items-center justify-center gap-2 text-gray-500">
        <AiOutlineLoading3Quarters className="animate-spin" /> Carregando…
      </div>
    );
  }

  if (estado === "ok") return <>{children}</>;

  if (confirmandoRecusa) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white ring-1 ring-gray-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-gray-900">
            Não concordo com os termos
          </h2>
          <p className="mt-3 text-sm text-gray-600">
            Sem o aceite não é possível utilizar o serviço de visualização e
            armazenamento de câmeras em nuvem. Você será desconectado.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
            <button
              onClick={() => setConfirmandoRecusa(false)}
              className="px-4 py-2 rounded-md ring-1 ring-gray-300 text-sm text-gray-700"
            >
              Voltar ao termo
            </button>
            <button
              onClick={sair}
              className="px-4 py-2 rounded-md bg-red-600 text-white text-sm hover:bg-red-700"
            >
              Sair do sistema
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center px-4 py-6">
      <div className="w-full max-w-3xl bg-white ring-1 ring-gray-200 rounded-lg flex flex-col max-h-[calc(100vh-3rem)]">
        <div className="p-5 border-b">
          <h1 className="flex items-center gap-2 text-lg font-bold text-gray-900">
            <MdVideocam className="text-indigo-600" /> {TERMS_TITLE}
          </h1>
          <p className="mt-1 text-sm text-gray-600">{TERMS_SUBTITLE}</p>
          <p className="mt-1 text-xs text-gray-400">
            WIP TELECOM MULTIMIDIA EIRELI — versão {TERMS_VERSION}, publicado em{" "}
            {TERMS_PUBLISHED_AT}
          </p>
        </div>

        <div
          ref={textoRef}
          onScroll={aoRolar}
          className="flex-1 overflow-y-auto p-5 text-sm text-gray-700 space-y-3"
        >
          {linhas.map((linha, i) => {
            if (linha.tipo === "titulo")
              return (
                <h2
                  key={i}
                  className="pt-3 text-sm font-bold text-gray-900 uppercase"
                >
                  {linha.texto}
                </h2>
              );
            if (linha.tipo === "opcao")
              return (
                <p
                  key={i}
                  className="pt-2 font-semibold text-gray-900"
                >
                  {linha.texto}
                </p>
              );
            if (linha.tipo === "item")
              return (
                <p key={i} className="pl-4 relative before:content-['•'] before:absolute before:left-0">
                  {linha.texto}
                </p>
              );
            return <p key={i}>{linha.texto}</p>;
          })}
        </div>

        <div className="p-5 border-t space-y-3">
          {!lido && (
            <p className="text-xs text-amber-600">
              Role o texto até o fim para habilitar o aceite.
            </p>
          )}
          {erro && <p className="text-sm text-red-600">{erro}</p>}
          <p className="text-xs text-gray-500">
            O aceite é registrado com data, hora, endereço IP, identificação da
            conta e versão do termo, para fins de comprovação.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={concordar}
              disabled={!lido || enviando}
              className="flex-1 px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:bg-gray-300"
            >
              {enviando ? "Registrando..." : "CONCORDO COM TODOS OS TERMOS"}
            </button>
            <button
              onClick={() => setConfirmandoRecusa(true)}
              disabled={enviando}
              className="flex-1 px-4 py-2 rounded-md ring-1 ring-gray-300 text-sm text-gray-700 disabled:opacity-50"
            >
              NÃO CONCORDO COM OS TERMOS
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsGate;
